// Single entry point for BLE remote input coming from the native iOS app.
//
// Native calls window.onRemoteEvent(json) for every remote press (while no Unity training is on screen)
// and for every connection status change. Everything in the web app should get remote input from here:
//   - subscribeRemoteButton(handler)  -> called once per press
//   - subscribeRemoteStatus(handler)  -> called on every status update (connected, battery, ...)
//   - window "remote-button" CustomEvent (detail = RemoteButtonEvent), for non-React code
// Do not set window.onRemoteEvent anywhere else.

import {
  parseRemoteEvent,
  RemoteButtonEvent,
  RemoteScanResultEvent,
  RemoteStatusEvent,
} from "../models/RemoteInput";
import { logWebOnMobile, requestRemoteStatus } from "../utils/mobile-bridge";

type Listener<T> = (event: T) => void;

const buttonListeners = new Set<Listener<RemoteButtonEvent>>();
const statusListeners = new Set<Listener<RemoteStatusEvent>>();
const scanListeners = new Set<Listener<RemoteScanResultEvent>>();

let initialized = false;
let lastSeq = -1;
let lastStatus: RemoteStatusEvent | null = null;

// Call once at app start (index.tsx).
export function initRemoteInput() {
  if (initialized) return;
  initialized = true;

  (window as any).onRemoteEvent = (json: string) => handleRemoteEvent(json);

  // Any status reply means this native build supports the remote.
  requestRemoteStatus();
}

function handleRemoteEvent(json: string) {
  const event = parseRemoteEvent(json);
  if (!event) {
    console.warn("Ignored unrecognized remote event from mobile:", json);
    return;
  }

  switch (event.type) {
    case "button": {
      // Drop duplicates (seq only increases within one app launch).
      if (event.seq <= lastSeq) return;
      lastSeq = event.seq;

      const message = `${event.button} input received from mobile in web (seq ${event.seq})`;
      console.log(message);
      // Also shows up in the Xcode console as "method_name : logWebOnMobile" followed by the message.
      logWebOnMobile(message);

      buttonListeners.forEach((listener) => listener(event));
      window.dispatchEvent(new CustomEvent("remote-button", { detail: event }));
      break;
    }

    case "status": {
      lastStatus = event;
      const message = `Remote status received from mobile in web: connected=${event.connected} paired=${event.paired} bluetooth=${event.bluetooth}`;
      console.log(message);
      logWebOnMobile(message);
      statusListeners.forEach((listener) => listener(event));
      break;
    }

    case "scanResult":
      scanListeners.forEach((listener) => listener(event));
      break;
  }
}

// Returns an unsubscribe function; call it on cleanup (e.g. in a useEffect return).
export function subscribeRemoteButton(listener: Listener<RemoteButtonEvent>): () => void {
  buttonListeners.add(listener);
  return () => buttonListeners.delete(listener);
}

// The listener is called right away with the last known status, if any.
export function subscribeRemoteStatus(listener: Listener<RemoteStatusEvent>): () => void {
  statusListeners.add(listener);
  if (lastStatus) listener(lastStatus);
  return () => statusListeners.delete(listener);
}

export function subscribeRemoteScanResults(listener: Listener<RemoteScanResultEvent>): () => void {
  scanListeners.add(listener);
  return () => scanListeners.delete(listener);
}

// Null until native has replied (old native builds never reply, so remote UI should stay hidden).
export function getRemoteStatus(): RemoteStatusEvent | null {
  return lastStatus;
}
